import React, { useMemo } from 'react';
import { demeterMarketIndicatorsApi } from '../../../Apis/Apis';
import scssVariables from '../../../Config.module.scss';
import { MarketIndicatorModel } from '../../../Generated/Raven-Demeter';
import useApi from '../../Apis/Hooks/useApiHook';
import { chartColors } from '../../Components/Charts/ChartDefinitions';
import useSearchParameters from '../../Components/Navigation/Hooks/useSearchParametersHook';
import formattingService from '../../Services/Formatting/FormattingService';
import useLanguage from '../../Services/Language/useLanguageHook';
import styles from './HedgeMonitorProPage.module.scss';

interface IHedgeMonitorProTableProps {
    marketIndicator?: MarketIndicatorModel;
}

const maximumQuartersToDisplay = 4;

const HedgeMonitorProTable: React.FC<IHedgeMonitorProTableProps> = (props: IHedgeMonitorProTableProps) => {
    // Application hooks and constants.
    const [translations] = useLanguage();
    const [searchParameters] = useSearchParameters();

    const [, , listMarketIndicatorSummariesResponse] = useApi(() => demeterMarketIndicatorsApi.listMarketIndicatorSummaries());
    const currentHedgeMonitorProRow = listMarketIndicatorSummariesResponse?.rows?.find((x) => x.marketIndicatorGuid === searchParameters.marketIndicatorGuid);

    const marketIndicatorsDecileColor = useMemo(
        () => chartColors.priceDecileColorsRuleSetTwo.find((x) => (x.decileRank - 1) * 10 <= (props.marketIndicator?.priceDecile ?? 0))?.color,
        [props.marketIndicator],
    );

    const contractMonthYear = useMemo(() => {
        if (!props.marketIndicator || !props.marketIndicator.currentPriceContractMonth || !props.marketIndicator.currentPriceContractYear) {
            return '';
        }

        return `${formattingService.toMonthYear(
            new Date(props.marketIndicator.currentPriceContractYear, props.marketIndicator.currentPriceContractMonth - 1, 1),
        )}`;
    }, [props.marketIndicator]);

    return (
        <div className={styles.hedge_monitor_pro_summary_table}>
            <div className={styles.hedge_monitor_pro_summary_table_row}>
                <div className={styles.hedge_monitor_pro_summary_table_header}>
                    {formattingService.toShortDayMonthYear(props.marketIndicator?.currentPriceAsOfDate ?? props.marketIndicator?.asOfDate)}
                </div>
                <div className={styles.hedge_monitor_pro_summary_table_header}>{translations.words.price}</div>
                <div className={styles.hedge_monitor_pro_summary_table_header}>{translations.words.percentile}</div>
            </div>
            <div className={styles.hedge_monitor_pro_summary_table_row}>
                <div className={styles.hedge_monitor_pro_summary_table_header}>{contractMonthYear}</div>
                <div className={styles.hedge_monitor_pro_summary_table_value}>{formattingService.formatPriceNumber(props.marketIndicator?.currentPrice)}</div>
                <div className={styles.hedge_monitor_pro_summary_table_value} style={{ backgroundColor: marketIndicatorsDecileColor }}>
                    {formattingService.formatNumber(props.marketIndicator?.priceDecile, { precision: 0 })}
                </div>
            </div>

            {currentHedgeMonitorProRow?.quarterDeciles.length! > 0 && (
                <>
                    {currentHedgeMonitorProRow?.quarterDeciles.slice(0, maximumQuartersToDisplay).map((x) => {
                        const marketIndicatorsSummaryDecileColor = chartColors.priceDecileColorsRuleSetTwo.find(
                            (y) => (y.decileRank - 1) * 10 <= (x.decile ?? 0),
                        )?.color;

                        return (
                            <div className={styles.hedge_monitor_pro_summary_table_row}>
                                <div className={styles.hedge_monitor_pro_summary_table_header}>
                                    {translations.words.quarterShort}
                                    {x.quarter} {x.year}
                                </div>
                                <div className={styles.hedge_monitor_pro_summary_table_value}>{formattingService.formatPriceNumber(x.value)}</div>
                                <div className={styles.hedge_monitor_pro_summary_table_value} style={{ backgroundColor: marketIndicatorsSummaryDecileColor }}>
                                    {formattingService.formatNumber(x.decile, { precision: 0 })}
                                </div>
                            </div>
                        );
                    })}
                    <div className={styles.hedge_monitor_pro_summary_table_row}>
                        <div className={styles.hedge_monitor_pro_summary_table_header}>{translations.hedgeMonitorPro.text.fiftiethDecile}</div>
                        <div
                            className={styles.hedge_monitor_pro_summary_table_value_double}
                            style={{ backgroundColor: scssVariables.chartColor17, color: scssVariables.plainWhite }}
                        >
                            {currentHedgeMonitorProRow?.deciles.find((x) => x.decile === 50)?.value}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default HedgeMonitorProTable;
